/**
 * MessageInbox Component
 * Phase 6: Communication & Engagement
 * Conversation list with grouping and filtering
 */

import { useState, useMemo } from 'react';
import { useConversations } from '../hooks/useMessaging';
import {
  Inbox,
  Mail,
  MailOpen,
  Clock,
  CheckCircle2,
  Archive,
  Search,
  Filter,
  RefreshCw,
  Loader2,
  MessageSquare,
  Building2,
  ChevronRight
} from 'lucide-react';

// Time formatter
function formatTimeAgo(dateStr) {
  if (!dateStr) return '';

  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Truncate text
function truncate(text, maxLength = 80) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

// Filter tabs
const FILTER_TABS = [
  { id: 'all', label: 'All', icon: Inbox },
  { id: 'unread', label: 'Unread', icon: Mail },
  { id: 'replied', label: 'Replied', icon: CheckCircle2 },
  { id: 'awaiting', label: 'Awaiting Reply', icon: Clock }
];

// Conversation row
function ConversationRow({ conversation, userType, isSelected, onClick }) {
  const unreadCount = userType === 'seller'
    ? conversation.unread_for_seller
    : conversation.unread_for_buyer;

  const isUnread = unreadCount > 0;
  const otherPartyName = userType === 'seller'
    ? conversation.buyer_company
    : (conversation.seller_company || conversation.seller_company_cn);

  // Determine if awaiting reply
  const awaitingReply = userType === 'seller'
    ? conversation.last_message_sender === 'seller'
    : conversation.last_message_sender === 'buyer';

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
        isSelected ? 'bg-purple-50 border-l-4 border-l-purple-600' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar / Status indicator */}
        <div className="relative flex-shrink-0">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isUnread ? 'bg-purple-100' : 'bg-gray-100'
          }`}>
            <Building2 className={`w-5 h-5 ${isUnread ? 'text-purple-600' : 'text-gray-500'}`} />
          </div>
          {isUnread && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-purple-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <span className={`font-medium truncate ${isUnread ? 'text-gray-900' : 'text-gray-700'}`}>
              {otherPartyName}
            </span>
            <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
              {formatTimeAgo(conversation.last_message_at)}
            </span>
          </div>

          {/* Subject */}
          {conversation.subject && (
            <div className={`text-sm truncate ${isUnread ? 'font-medium text-gray-800' : 'text-gray-600'}`}>
              {conversation.subject}
            </div>
          )}

          {/* Preview */}
          <div className="text-sm text-gray-500 truncate mt-0.5">
            {conversation.last_message_sender === (userType === 'seller' ? 'seller' : 'buyer') && (
              <span className="text-gray-400">You: </span>
            )}
            {truncate(conversation.last_message_preview, 60)}
          </div>

          {/* Tags */}
          <div className="flex items-center gap-2 mt-2">
            {conversation.asset_name && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                {conversation.asset_name}
              </span>
            )}
            {awaitingReply && !isUnread && (
              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Awaiting reply
              </span>
            )}
          </div>
        </div>

        {/* Arrow */}
        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
      </div>
    </button>
  );
}

// Empty state
function EmptyState({ filter }) {
  const messages = {
    all: 'No conversations yet',
    unread: 'No unread messages',
    replied: 'No replied conversations',
    awaiting: 'No conversations awaiting reply'
  };

  return (
    <div className="py-12 text-center">
      <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
      <p className="text-gray-500">{messages[filter] || messages.all}</p>
      {filter === 'all' && (
        <p className="text-sm text-gray-400 mt-1">
          Start a conversation by messaging a buyer from their profile
        </p>
      )}
    </div>
  );
}

// Main MessageInbox component
export function MessageInbox({
  userId,
  userType,
  selectedConversationId,
  onSelectConversation,
  className = ''
}) {
  const { conversations, loading, error, unreadCount, refresh } = useConversations(userId, userType);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter conversations
  const filteredConversations = useMemo(() => {
    let filtered = [...conversations];

    // Apply filter
    if (filter === 'unread') {
      const unreadKey = userType === 'seller' ? 'unread_for_seller' : 'unread_for_buyer';
      filtered = filtered.filter(c => c[unreadKey] > 0);
    } else if (filter === 'replied') {
      // Conversations where the other party has replied
      filtered = filtered.filter(c =>
        userType === 'seller'
          ? c.buyer_message_count > 0
          : c.seller_message_count > 0
      );
    } else if (filter === 'awaiting') {
      // Conversations where we're waiting for a reply
      filtered = filtered.filter(c => {
        const ourLastMessage = userType === 'seller'
          ? c.last_message_sender === 'seller'
          : c.last_message_sender === 'buyer';
        const unreadKey = userType === 'seller' ? 'unread_for_seller' : 'unread_for_buyer';
        return ourLastMessage && c[unreadKey] === 0;
      });
    }

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        c.buyer_company?.toLowerCase().includes(query) ||
        c.seller_company?.toLowerCase().includes(query) ||
        c.seller_company_cn?.toLowerCase().includes(query) ||
        c.subject?.toLowerCase().includes(query) ||
        c.asset_name?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [conversations, filter, searchQuery, userType]);

  // Count for each filter
  const filterCounts = useMemo(() => {
    const unreadKey = userType === 'seller' ? 'unread_for_seller' : 'unread_for_buyer';

    return {
      all: conversations.length,
      unread: conversations.filter(c => c[unreadKey] > 0).length,
      replied: conversations.filter(c =>
        userType === 'seller' ? c.buyer_message_count > 0 : c.seller_message_count > 0
      ).length,
      awaiting: conversations.filter(c => {
        const ourLastMessage = userType === 'seller'
          ? c.last_message_sender === 'seller'
          : c.last_message_sender === 'buyer';
        return ourLastMessage && c[unreadKey] === 0;
      }).length
    };
  }, [conversations, userType]);

  if (loading) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 ${className}`}>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border border-gray-200 flex flex-col ${className}`}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Inbox className="w-5 h-5 text-gray-600" />
            <h2 className="font-semibold text-gray-900">Messages</h2>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                {unreadCount} new
              </span>
            )}
          </div>
          <button
            onClick={refresh}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search conversations..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex border-b border-gray-200 px-2 overflow-x-auto">
        {FILTER_TABS.map(tab => {
          const Icon = tab.icon;
          const count = filterCounts[tab.id];
          const isActive = filter === tab.id;

          return (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                isActive
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              {count > 0 && (
                <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                  isActive ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto">
        {filteredConversations.length === 0 ? (
          <EmptyState filter={filter} />
        ) : (
          <div>
            {filteredConversations.map(conversation => (
              <ConversationRow
                key={conversation.id}
                conversation={conversation}
                userType={userType}
                isSelected={selectedConversationId === conversation.id}
                onClick={() => onSelectConversation(conversation)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Error state */}
      {error && (
        <div className="p-4 bg-red-50 border-t border-red-200 text-red-700 text-sm">
          Error loading messages: {error}
        </div>
      )}
    </div>
  );
}

export default MessageInbox;
